import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Wordmark } from '@cowoo/shared/ui-web';
import { auth } from '../../lib/auth';
import { useAdminStore } from '../../lib/store';
import { useSignOut } from './useSignOut';

/**
 * Shown when `/admin/me` answers `403 not_admin` (10-web-admin § 2.2).
 *
 * The address matters here: an invite is bound to one, and the usual reason to
 * land on this page is having signed in with a different one. Signing out has
 * to be reachable, or the wrong account is a dead end.
 *
 * The address comes from OtterAuth, not from `/admin/me`: the whole point of
 * this screen is that `/admin/me` refused to answer.
 */
export function NoAccess() {
  const { t } = useTranslation();
  const signOut = useSignOut();
  const token = useAdminStore((state) => state.token);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void auth
      .me()
      .then((user) => {
        if (!cancelled) setEmail(user.identifiers.emails[0]?.email ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <main className="bg-fog flex min-h-screen items-center justify-center px-5 py-12">
      <div className="rounded-webCard border-line flex w-full max-w-[520px] flex-col gap-4 border bg-white p-10">
        <div className="flex items-center gap-2.5 pb-2">
          <span className="text-teal">
            <Wordmark size={26} />
          </span>
          <span className="bg-teal-tint text-teal-dark font-body rounded-pill px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.1em]">
            {t('admin.badge')}
          </span>
        </div>

        <span className="bg-warn-bg rounded-input flex h-12 w-12 items-center justify-center" aria-hidden>
          <LockIcon />
        </span>

        <h1 className="text-h1 text-ink">{t('admin.noAccess.body')}</h1>
        {email ? <p className="text-webSmall text-muted">{t('admin.noAccess.hint', { email })}</p> : null}

        <Button variant="secondary" onClick={() => void signOut()} className="mt-2" full>
          {t('admin.signOut')}
        </Button>
      </div>
    </main>
  );
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      className="text-warn-text block"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </svg>
  );
}
